import { PDFDocument, StandardFonts, degrees, rgb } from 'pdf-lib';
import { loadPdfDocument } from './pdfRender.js';

const RASTER_SCALE = 2;

const FONT_MAP = {
  Helvetica: { regular: StandardFonts.Helvetica, bold: StandardFonts.HelveticaBold, italic: StandardFonts.HelveticaOblique, bolditalic: StandardFonts.HelveticaBoldOblique },
  Times: { regular: StandardFonts.TimesRoman, bold: StandardFonts.TimesRomanBold, italic: StandardFonts.TimesRomanItalic, bolditalic: StandardFonts.TimesRomanBoldItalic },
  Courier: { regular: StandardFonts.Courier, bold: StandardFonts.CourierBold, italic: StandardFonts.CourierOblique, bolditalic: StandardFonts.CourierBoldOblique },
};

export const FONT_FAMILIES = Object.keys(FONT_MAP);

function pickFont(family, bold, italic) {
  const fam = FONT_MAP[family] || FONT_MAP.Helvetica;
  const key = bold && italic ? 'bolditalic' : bold ? 'bold' : italic ? 'italic' : 'regular';
  return fam[key];
}

export function fontFamilyToCss(family) {
  if (family === 'Times') return '"Times New Roman", Times, serif';
  if (family === 'Courier') return '"Courier New", Courier, monospace';
  return 'Helvetica, Arial, sans-serif';
}

export async function buildPagesMeta(bytes) {
  const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
  const count = doc.getPageCount();
  const pages = [];
  for (let i = 0; i < count; i++) {
    const p = doc.getPage(i);
    const { width, height } = p.getSize();
    pages.push({
      id: crypto.randomUUID(),
      originalIndex: i,
      rotation: p.getRotation().angle % 360,
      width,
      height,
    });
  }
  return pages;
}

function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return rgb(0, 0, 0);
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return rgb(0, 0, 0);
  return rgb(((num >> 16) & 0xff) / 255, ((num >> 8) & 0xff) / 255, (num & 0xff) / 255);
}

async function dataUrlToBytes(dataUrl) {
  const res = await fetch(dataUrl);
  return new Uint8Array(await res.arrayBuffer());
}

async function rasterizePage(pdfjsDoc, originalIndex, scale = RASTER_SCALE) {
  const page = await pdfjsDoc.getPage(originalIndex + 1);
  const viewport = page.getViewport({ scale, rotation: 0 });
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext('2d', { alpha: false });
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
  return dataUrlToBytes(dataUrl);
}

export async function exportPdf(bytes, pages, annotations = {}) {
  if (!bytes || !bytes.length) throw new Error('exportPdf: empty source bytes');
  const srcCopy = bytes.slice(0);
  const src = await PDFDocument.load(srcCopy, { ignoreEncryption: true });
  const out = await PDFDocument.create();

  const fontCache = new Map();
  const getFont = async (key) => {
    if (fontCache.has(key)) return fontCache.get(key);
    const f = await out.embedFont(key);
    fontCache.set(key, f);
    return f;
  };

  const useRaster = src.isEncrypted;

  let outPages;
  if (useRaster) {
    const pdfjsDoc = await loadPdfDocument(bytes);
    outPages = [];
    for (let i = 0; i < pages.length; i++) {
      const meta = pages[i];
      const jpegBytes = await rasterizePage(pdfjsDoc, meta.originalIndex);
      const img = await out.embedJpg(jpegBytes);
      const page = out.addPage([meta.width, meta.height]);
      page.setRotation(degrees(meta.rotation));
      page.drawImage(img, { x: 0, y: 0, width: meta.width, height: meta.height });
      outPages.push(page);
    }
  } else {
    const indices = pages.map((p) => p.originalIndex);
    const copied = await out.copyPages(src, indices);
    outPages = [];
    for (let i = 0; i < copied.length; i++) {
      const page = copied[i];
      const meta = pages[i];
      page.setRotation(degrees(meta.rotation));
      out.addPage(page);
      outPages.push(page);
    }
  }

  for (let i = 0; i < pages.length; i++) {
    const page = outPages[i];
    const meta = pages[i];

    const list = annotations[meta.id] || [];
    const { width: pw, height: ph } = page.getSize();

    for (const a of list) {
      if (a.type === 'rect') {
        const y = ph - a.y - a.h;
        page.drawRectangle({
          x: a.x,
          y,
          width: a.w,
          height: a.h,
          color: hexToRgb(a.color || '#ffffff'),
          opacity: a.opacity ?? 1,
          borderWidth: 0,
        });
      } else if (a.type === 'highlight') {
        const y = ph - a.y - a.h;
        page.drawRectangle({
          x: a.x,
          y,
          width: a.w,
          height: a.h,
          color: hexToRgb(a.color || '#fde047'),
          opacity: a.opacity ?? 0.4,
          borderWidth: 0,
          blendMode: 'Multiply',
        });
      } else if (a.type === 'shape') {
        const kind = a.shape || 'rect';
        const sw = a.strokeWidth || 2;
        const col = hexToRgb(a.color || '#ef4444');
        if (kind === 'rect') {
          const y = ph - a.y - a.h;
          page.drawRectangle({
            x: a.x,
            y,
            width: a.w,
            height: a.h,
            color: rgb(1, 1, 1),
            opacity: 0,
            borderColor: col,
            borderWidth: sw,
          });
        } else if (kind === 'fillrect') {
          // Solid filled rectangle ("white zone") covering content.
          const y = ph - a.y - a.h;
          page.drawRectangle({
            x: a.x,
            y,
            width: a.w,
            height: a.h,
            color: col,
            borderWidth: 0,
          });
        } else if (kind === 'ellipse') {
          const cx = a.x + a.w / 2;
          const cy = ph - (a.y + a.h / 2);
          page.drawEllipse({
            x: cx,
            y: cy,
            xScale: a.w / 2,
            yScale: a.h / 2,
            borderColor: col,
            borderWidth: sw,
            color: rgb(1, 1, 1),
            opacity: 0,
          });
        } else if (kind === 'circle') {
          const cx = a.cx;
          const cy = ph - a.cy;
          page.drawCircle({
            x: cx,
            y: cy,
            size: a.r,
            borderColor: col,
            borderWidth: sw,
            color: rgb(1, 1, 1),
            opacity: 0,
          });
        } else if (kind === 'line') {
          page.drawLine({
            start: { x: a.x1, y: ph - a.y1 },
            end: { x: a.x2, y: ph - a.y2 },
            thickness: sw,
            color: col,
          });
        }
      } else if (a.type === 'draw' && a.points && a.points.length > 1) {
        let d = '';
        for (let i = 0; i < a.points.length; i++) {
          const px = a.points[i].x;
          const py = ph - a.points[i].y;
          d += i === 0 ? `M ${px} ${py}` : ` L ${px} ${py}`;
        }
        page.drawSvgPath(d, {
          borderColor: hexToRgb(a.color || '#ef4444'),
          borderWidth: a.strokeWidth || 2,
          color: rgb(0, 0, 0),
          opacity: 0,
        });
      } else if (a.type === 'text') {
        const font = await getFont(pickFont(a.font || 'Helvetica', !!a.bold, !!a.italic));
        const size = a.size || 14;
        const txt = a.text || '';
        const textWidth = font.widthOfTextAtSize(txt, size);
        const drawX = a.x;
        const baselineY = ph - a.y - size;
        const textColor = hexToRgb(a.color || '#111111');
        const rot = a.rotation || 0;
        // The editor rotates the box around its centre. pdf-lib rotates
        // around the draw anchor, so rotate every anchor point around
        // that same centre to keep export and preview consistent.
        const bw = a.w || textWidth;
        const bh = a.h || size * 1.2;
        const cx = a.x + bw / 2;
        const cy = ph - a.y - bh / 2;
        const rad = (-rot * Math.PI) / 180; // CSS clockwise -> PDF ccw
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const rotPt = (x, y) => {
          if (!rot) return { x, y };
          const vx = x - cx;
          const vy = y - cy;
          return { x: cx + vx * cos - vy * sin, y: cy + vx * sin + vy * cos };
        };
        const rotateOpt = rot ? { rotate: degrees(rot) } : {};

        if (a.background) {
          const ry = ph - a.y - bh;
          const p = rotPt(a.x, ry);
          page.drawRectangle({
            x: p.x,
            y: p.y,
            width: bw,
            height: bh,
            color: hexToRgb(a.background),
            borderWidth: 0,
            ...rotateOpt,
          });
        }
        const tp = rotPt(drawX, baselineY);
        page.drawText(txt, {
          x: tp.x,
          y: tp.y,
          size,
          font,
          color: textColor,
          lineHeight: size * 1.15,
          ...rotateOpt,
        });
        if (a.underline && txt) {
          const uY = baselineY - size * 0.12;
          const s1 = rotPt(drawX, uY);
          const e1 = rotPt(drawX + textWidth, uY);
          page.drawLine({
            start: s1,
            end: e1,
            thickness: Math.max(0.6, size * 0.06),
            color: textColor,
          });
        }
        if (a.strike && txt) {
          const sY = baselineY + size * 0.32;
          const s2 = rotPt(drawX, sY);
          const e2 = rotPt(drawX + textWidth, sY);
          page.drawLine({
            start: s2,
            end: e2,
            thickness: Math.max(0.6, size * 0.06),
            color: textColor,
          });
        }
      } else if (a.type === 'image') {
        const data = await dataUrlToBytes(a.dataUrl);
        const isPng = a.dataUrl.startsWith('data:image/png');
        const img = isPng ? await out.embedPng(data) : await out.embedJpg(data);
        const y = ph - a.y - a.h;
        page.drawImage(img, { x: a.x, y, width: a.w, height: a.h, opacity: a.opacity ?? 1 });
      }
    }
  }

  const u8 = await out.save({ useObjectStreams: false });
  if (!u8 || !u8.length) throw new Error('exportPdf: empty output bytes');
  return new Blob([u8], { type: 'application/pdf' });
}

// Merge PDFs. Each input may carry an optional `pageIndices` array to
// merge only a subset of its pages (in the given order).
export async function mergePdfs(inputs) {
  const out = await PDFDocument.create();
  for (const { bytes, pageIndices } of inputs) {
    const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
    const all = doc.getPageIndices();
    const wanted =
      pageIndices && pageIndices.length
        ? pageIndices.filter((i) => i >= 0 && i < all.length)
        : all;
    const pages = await out.copyPages(doc, wanted);
    pages.forEach((p) => out.addPage(p));
  }
  const u8 = await out.save({ useObjectStreams: true });
  return new Blob([u8], { type: 'application/pdf' });
}


// File extensions that can be merged into a letter PDF.
export const MERGEABLE_ATTACHMENT_RE = /\.(pdf|png|jpe?g|webp|docx)$/i;
export function isMergeableAttachment(file) {
  if (!file) return false;
  const t = file.type || '';
  if (t === 'application/pdf' || t.startsWith('image/')) return true;
  if (t === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return true;
  return MERGEABLE_ATTACHMENT_RE.test(file.name || '');
}

const A4 = { w: 595.28, h: 841.89 };

// Append a .docx file: render it with docx-preview, rasterise each
// rendered page with html2canvas, embed it as a PDF page.
async function appendDocx(doc, buf) {
  const { renderAsync } = await import('docx-preview');
  const html2canvas = (await import('html2canvas')).default;
  const host = document.createElement('div');
  host.style.cssText =
    'position:fixed;left:-99999px;top:0;width:794px;background:#ffffff;';
  document.body.appendChild(host);
  try {
    await renderAsync(buf, host, null, {
      className: 'docx',
      inWrapper: true,
      breakPages: true,
      ignoreWidth: false,
      ignoreHeight: false,
    });
    const sections = host.querySelectorAll('.docx-wrapper > section, section.docx');
    const targets = sections.length ? Array.from(sections) : [host];
    for (const sec of targets) {
      const canvas = await html2canvas(sec, { scale: 2, backgroundColor: '#ffffff' });
      const jpeg = await dataUrlToBytes(canvas.toDataURL('image/jpeg', 0.92));
      const img = await doc.embedJpg(jpeg);
      const page = doc.addPage([A4.w, A4.h]);
      const scale = Math.min(A4.w / img.width, A4.h / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      page.drawImage(img, { x: (A4.w - w) / 2, y: A4.h - h, width: w, height: h });
    }
  } finally {
    host.remove();
  }
}

// Append an attachment file (PDF, image or Word .docx) as page(s).
async function appendAttachment(doc, file) {
  if (!file) return;
  const name = (file.name || '').toLowerCase();
  const type = file.type || '';
  const buf = new Uint8Array(await file.arrayBuffer());
  if (type === 'application/pdf' || name.endsWith('.pdf')) {
    const src = await PDFDocument.load(buf, { ignoreEncryption: true });
    const pages = await doc.copyPages(src, src.getPageIndices());
    pages.forEach((p) => doc.addPage(p));
  } else if (type.startsWith('image/') || /\.(png|jpe?g|webp)$/.test(name)) {
    const isPng = type === 'image/png' || name.endsWith('.png');
    let img;
    try {
      img = isPng ? await doc.embedPng(buf) : await doc.embedJpg(buf);
    } catch {
      return; // unsupported image encoding
    }
    const p = doc.addPage([A4.w, A4.h]);
    const scale = Math.min((A4.w - 80) / img.width, (A4.h - 80) / img.height, 1);
    const w = img.width * scale;
    const h = img.height * scale;
    p.drawImage(img, { x: (A4.w - w) / 2, y: (A4.h - h) / 2, width: w, height: h });
  } else if (name.endsWith('.docx')) {
    await appendDocx(doc, buf);
  }
}

// Generate a formatted French business letter PDF from a letter object.
// letter = { sender, recipient, city, subject, body, attachments, signature }
//   attachments: array of { id, label, file? }
export async function generateLetterPdf(letter) {
  const doc = await PDFDocument.create();
  const W = 595.28;
  const H = 841.89; // A4
  const M = 64;
  const page = doc.addPage([W, H]);
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const ink = rgb(0.07, 0.07, 0.09);
  const SIZE = 11;
  const LH = 15;

  const wrap = (text, maxW, f, s) => {
    const out = [];
    for (const para of String(text || '').split('\n')) {
      if (!para.trim()) {
        out.push('');
        continue;
      }
      let line = '';
      for (const word of para.split(/\s+/)) {
        const test = line ? `${line} ${word}` : word;
        if (f.widthOfTextAtSize(test, s) > maxW && line) {
          out.push(line);
          line = word;
        } else {
          line = test;
        }
      }
      if (line) out.push(line);
    }
    return out;
  };

  // Draw a multi-line block; the first non-empty line is bold.
  const drawBlock = (text, x, y) => {
    const lines = String(text || '').split('\n');
    let cy = y;
    let firstDone = false;
    for (const ln of lines) {
      if (ln) {
        const useBold = !firstDone;
        page.drawText(ln, { x, y: cy, size: SIZE, font: useBold ? bold : font, color: ink });
        firstDone = true;
      }
      cy -= LH;
    }
    return cy;
  };

  const draw = (lines, x, y, f = font, s = SIZE, lh = LH) => {
    let cy = y;
    for (const ln of lines) {
      if (ln) page.drawText(ln, { x, y: cy, size: s, font: f, color: ink });
      cy -= lh;
    }
    return cy;
  };

  // Sender — top left, first line bold
  const yAfterSender = drawBlock(letter.sender, M, H - M);

  // Recipient — right column (pushed further right), first line bold
  const recipX = W - M - 190;
  const recipLines = String(letter.recipient || '').split('\n').length;
  drawBlock(letter.recipient, recipX, H - M - 70);

  // Place + date — left, below both blocks
  const dateStr = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const place = letter.city ? `${letter.city}, le ${dateStr}` : `Le ${dateStr}`;
  let cursor = Math.min(yAfterSender, H - M - 70 - recipLines * LH) - 24;
  page.drawText(place, { x: M, y: cursor, size: SIZE, font, color: ink });
  cursor -= LH + 14;

  // Subject
  if (letter.subject) {
    page.drawText('Objet : ', { x: M, y: cursor, size: SIZE, font: bold, color: ink });
    const objX = M + bold.widthOfTextAtSize('Objet : ', SIZE);
    const objLines = wrap(letter.subject, W - 2 * M - (objX - M), font, SIZE);
    objLines.forEach((ln, i) => {
      page.drawText(ln, { x: i === 0 ? objX : M, y: cursor - i * LH, size: SIZE, font, color: ink });
    });
    cursor -= objLines.length * LH + 6;
  }

  // Attachments line — labels joined, "PJ :" prefix
  const attachLabels = Array.isArray(letter.attachments)
    ? letter.attachments.map((a) => a.label).filter(Boolean)
    : letter.attachments
      ? [String(letter.attachments)]
      : [];
  if (attachLabels.length === 1) {
    // Single attachment — inline after the label.
    page.drawText('PJ : ', { x: M, y: cursor, size: SIZE, font: bold, color: ink });
    const pjX = M + bold.widthOfTextAtSize('PJ : ', SIZE);
    const pjLines = wrap(attachLabels[0], W - 2 * M - (pjX - M), font, SIZE);
    pjLines.forEach((ln, i) => {
      page.drawText(ln, { x: i === 0 ? pjX : M, y: cursor - i * LH, size: SIZE, font, color: ink });
    });
    cursor -= pjLines.length * LH + 6;
  } else if (attachLabels.length > 1) {
    // Several attachments — a bullet list under the label.
    page.drawText('PJ :', { x: M, y: cursor, size: SIZE, font: bold, color: ink });
    cursor -= LH;
    const bulletX = M + 12;
    const textX = bulletX + 10;
    for (const label of attachLabels) {
      const lines = wrap(label, W - M - textX, font, SIZE);
      page.drawText('•', { x: bulletX, y: cursor, size: SIZE, font, color: ink });
      lines.forEach((ln, i) => {
        page.drawText(ln, { x: textX, y: cursor - i * LH, size: SIZE, font, color: ink });
      });
      cursor -= lines.length * LH;
    }
    cursor -= 6;
  }

  // Body
  cursor -= 18;
  const bodyLines = wrap(letter.body, W - 2 * M, font, SIZE);
  cursor = draw(bodyLines, M, cursor, font, SIZE, LH + 2);

  // Signature image — small, bottom left
  if (letter.signature && letter.signature.dataUrl) {
    const data = await dataUrlToBytes(letter.signature.dataUrl);
    const isPng = letter.signature.dataUrl.startsWith('data:image/png');
    const img = isPng ? await doc.embedPng(data) : await doc.embedJpg(data);
    const sw = 95;
    const ratio = letter.signature.w && letter.signature.h
      ? letter.signature.h / letter.signature.w
      : 0.4;
    const sh = sw * ratio;
    const sy = Math.max(M, cursor - 16 - sh);
    page.drawImage(img, { x: M, y: sy, width: sw, height: sh });
  }

  // Merge attachment files (PDF / image) after the letter page.
  if (Array.isArray(letter.attachments)) {
    for (const att of letter.attachments) {
      await appendAttachment(doc, att.file);
    }
  }

  const u8 = await doc.save({ useObjectStreams: false });
  return new Blob([u8], { type: 'application/pdf' });
}

export async function sha256Hex(blob) {
  const buf = await blob.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
